import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/utils/api'

export interface PendingOverride {
  id: string
  user_name: string
  username: string
  override_date: string
  override_type: string
  reason?: string
  location?: string
  days_until: number
  created_at: string
  all_day: boolean
  start_time?: string
  end_time?: string
}

// Query keys factory
export const availabilityKeys = {
  all: ['availability'] as const,
  overrides: () => [...availabilityKeys.all, 'overrides'] as const,
}

/**
 * Fetch pending availability overrides
 */
export function usePendingOverrides(enabled: boolean = true) {
  return useQuery({
    queryKey: availabilityKeys.overrides(),
    queryFn: async () => {
      // apiClient.get() already returns response.data, so the result IS the data array
      const allOverrides = await apiClient.get('/api/availability/overrides')
      // Filter for pending status only
      const overrides = Array.isArray(allOverrides) ? allOverrides : []
      return overrides.filter((o: PendingOverride) => o.status === 'pending') as PendingOverride[]
    },
    enabled,
    staleTime: 30 * 1000, // 30 seconds
    refetchOnWindowFocus: true,
  })
}

/**
 * Approve availability override
 */
export function useApproveOverride() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (overrideId: string) => {
      return await apiClient.post(`/api/availability/overrides/${overrideId}/approve`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: availabilityKeys.overrides() })
    },
  })
}

/**
 * Reject availability override
 */
export function useRejectOverride() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (overrideId: string) => {
      return await apiClient.post(`/api/availability/overrides/${overrideId}/reject`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: availabilityKeys.overrides() })
    },
  })
}
